import React, { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import { IonApp, IonRouterOutlet } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import CircleLoader from "react-spinners/CircleLoader";
import { QueryClient, QueryClientProvider } from "react-query";
import styled from "styled-components";

import configureStore from "./configureStore";
import { PersistorContext } from "./context";

import { PrivateRoute } from "./lib/authentication";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CGroupsSideMenuItemSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import "./index.css";

import "./theme/react-grid-layout.css";
import "./theme/react-resizable.css";

import "typeface-rokkitt";
import "typeface-secular-one";
import "typeface-poppins";
import "typeface-aguafina-script";
import "@fontsource/roboto";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

Sentry.init({
  dsn:
    "https://3bf2bf11d9db4bd2aa49666569f7d6c1@o316671.ingest.sentry.io/5956339",
  integrations: [new Integrations.BrowserTracing()],
});

const { store, persistor } = configureStore({});

const LuxuryFrontPage = lazy(() => import("./pages/luxury-front-page"));
const LuxuryLoginPage = lazy(() => import("./pages/luxury-login-page"));
const LuxuryRegisterPage = lazy(() => import("./pages/luxury-register-page"));
const LuxuryAuthenticateItemPage = lazy(() =>
  import("./pages/luxury-authenticate-item-page")
);
const LuxuryShopPage = lazy(() => import("./pages/luxury-shop-page"));
const LuxurySellPage = lazy(() => import("./pages/luxury-sell-page"));
const LuxuryContactUsPage = lazy(() =>
  import("./pages/luxury-contact-us-page")
);
const LuxuryCollectionPage = lazy(() =>
  import("./pages/luxury-collection-page")
);
const LuxuryResetPasswordPage = lazy(() =>
  import("./pages/luxury-reset-password-page")
);
const LuxuryProfilePage = lazy(() => import("./pages/luxury-profile-page"));
const LuxuryPublicAuthenticityPage = lazy(() =>
  import("./pages/luxury-public-authenticity-page")
);
const LuxuryCartPage = lazy(() => import("./pages/luxury-cart-page"));
const LuxuryCheckoutPage = lazy(() => import("./pages/luxury-checkout-page"));
const LuxuryItemListingDetailsPage = lazy(() =>
  import("./pages/luxury-item-listing-details-page")
);
const LuxuryActivateAccountPage = lazy(() =>
  import("./pages/luxury-activate-account-page")
);
const LuxuryResetPasswordConfirm = lazy(() =>
  import("./pages/luxury-reset-password-confirm-page")
);
const LuxuryResendActivationPage = lazy(() =>
  import("./pages/luxury-resend-activation-page")
);
const StandardCheckoutPage = lazy(() => import("./pages/checkout-page"));
const CheckoutSuccessPage = lazy(() => import("./pages/checkout-success-page"));

const SuspenseLoadingContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000000;
`;
const SuspenseLogoImage = styled.img``;

const SuspenseLoading = (props) => {
  return (
    <SuspenseLoadingContainer>
      <SuspenseLogoImage src="assets/images/luxury_station_splash.png" />
      {/* <CircleLoader color={"#ba8911"} loading={true} size={150} /> */}
    </SuspenseLoadingContainer>
  );
};

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <PersistorContext.Provider value={persistor}>
            <IonApp>
              <Suspense fallback={<SuspenseLoading />}>
                <IonReactRouter forceRefresh={true}>
                  <IonRouterOutlet id="main">
                    <Route
                      path="/activate"
                      component={LuxuryActivateAccountPage}
                      exact={true}
                    />
                    <Route
                      path="/reset-password"
                      component={LuxuryResetPasswordPage}
                      exact={true}
                    />
                    <Route
                      path="/reset-password/confirm"
                      component={LuxuryResetPasswordConfirm}
                      exact={true}
                    />
                    <Route
                      path="/resend-activation"
                      component={LuxuryResendActivationPage}
                      exact={true}
                    />
                    <Route exact path="/login" component={LuxuryLoginPage} />
                    <Route
                      exact
                      path="/register"
                      component={LuxuryRegisterPage}
                    />
                    <PrivateRoute
                      exact
                      path="/authenticate-item"
                      component={LuxuryAuthenticateItemPage}
                    />
                    <PrivateRoute
                      exact
                      path="/collection"
                      component={LuxuryCollectionPage}
                    />
                    <PrivateRoute
                      exact
                      path="/sell"
                      component={LuxurySellPage}
                    />
                    <Route exact path="/shop" component={LuxuryShopPage} />
                    <Route
                      exact
                      path="/contact-us"
                      component={LuxuryContactUsPage}
                    />
                    <PrivateRoute
                      exact
                      path="/profile"
                      component={LuxuryProfilePage}
                    />
                    <PrivateRoute
                      exact
                      path="/checkout"
                      component={StandardCheckoutPage}
                    />
                    <PrivateRoute
                      exact
                      path="/classic-checkout"
                      component={LuxuryCheckoutPage}
                    />
                    <Route
                      exact
                      path="/authenticity"
                      component={LuxuryPublicAuthenticityPage}
                    />
                    <Route exact path="/cart" component={LuxuryCartPage} />
                    <Route
                      exact
                      path="/item-details"
                      component={LuxuryItemListingDetailsPage}
                    />
                    <Route
                      exact
                      path="/checkout-success"
                      component={CheckoutSuccessPage}
                    />
                    <Route exact path="/home" component={LuxuryFrontPage} />
                    {
                      //////////////////////////////////////////////
                      //////////      DEFAULT ROUTE       //////////
                      //////////////////////////////////////////////
                    }
                    <Route exact path="/" component={LuxuryFrontPage} />
                  </IonRouterOutlet>
                </IonReactRouter>
              </Suspense>
            </IonApp>
          </PersistorContext.Provider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
};

export default App;
